/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IGroup, IGroupDelivery, IGroups, IOrderInformation, ISalesLine } from '@msdyn365-commerce-modules/order-management/src/common';

import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect, useState } from 'react';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { PriceComponent } from '@msdyn365-commerce/components';
import { IActionContext } from '@msdyn365-commerce/core';
import {
    CommerceProperty,
    CommercePropertyValue,
    ProductSearchCriteria,
    ProductSearchResult,
    SalesLine,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import get from 'lodash/get';
import { IOrderConfirmationProps, IOrderConfirmationResources } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import CartLine from './components/cartlineitem.component';

/* VSI Customization - START - 23/11/20 */
export interface ISalesInfoProps {
    itemCount: React.ReactElement;
    grossAmount: React.ReactElement;
}

export interface IOrderDate {
    dateLabel: string;
    orderCreatedDate: string;
}

export interface IVendorProducts {
    name: string | undefined;
    time: string | undefined;
    productList: number[];
}
export interface DeliveryAddressShip { 
    props: { 
        deliveryType: string; 
        address: { 
            Name: string; 
            Street: string; 
            City: string; 
            ZipCode: string; 
            Phone: string; 
        }; 
    }; 
};
/* VSI Customization - END - 18/12/20 */
// remove customixation for .29 version
// Converting functional component to simple function to reduce cycolmatic complexity by adding undefined in props and checking condition in functio instead of main FC
// tslint:disable-next-line:no-any
// const _orderConfirmationOrderSummary = (props: IOrderSummary | undefined, salesOrder: any) => {
//     if (!props) {
//         return;
//     }
//     const { orderSummaryProps, heading, subtotal, totalAmount, earnedPoints } = props;
//     const shippingChargeAmount = salesOrder.ShippingChargeAmount;
//     return (
//         <Node {...orderSummaryProps}>
//             {heading}
//             {subtotal}
//             {}
//             {/* {shipping} */}
//             <p className='ms-order-confirmation__order-summary-line-subtotal'>
//                 <span className='ms-order-confirmation__order-summary-label'>{'Home Delivery'}</span>
//                 <span className='msc-price msc-order-msc-price ms-order-confirmation__order-summary-price'>
//                     <span className='msc-price__actual'>{shippingChargeAmount > 0 ? `£${shippingChargeAmount.toFixed(2)}` : '£0.00'}</span>
//                 </span>
//             </p>
//             {totalAmount}
//             {earnedPoints}
//         </Node>
//     );
// };

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {/* VSI-Customization -- START
        Payment not required on order-confirmation page as per design
        */}
        {title}
        {methods}
        {/* VSI-Customization -- END */}
    </Node>
);
// Converting functional component to simple function to reduce cycolmatic complexity by adding undefined in props and checking condition in functio instead of main FC
const _orderConfirmationHelp = (props: IHelp | undefined) => {
    if (!props) {
        return;
    }
    const { helpProps, needHelpLabel, helpLineNumberLabel, contactNumber } = props;
    return (
        <Node {...helpProps}>
            {needHelpLabel}
            {helpLineNumberLabel}
            {contactNumber}
        </Node>
    );
};
/* VSI Customization - START - 23/11/20 */
const _getCountandPrice = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>): ISalesInfoProps => {
    const {
        data: { orderHydration },
        resources: { itemLabel, itemsLabel, orderTotalLabelText }
    } = props;
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    // items count
    let itemsCount = 0;
    salesOrder &&
        salesOrder.SalesLines &&
        salesOrder.SalesLines.map(salesLine => {
            const quantity = salesLine.Quantity;
            itemsCount = quantity ? itemsCount + quantity : itemsCount;
        });
    // format price
    const grossAmount = salesOrder && salesOrder.GrossAmount;
    const totalAmount = grossAmount && props.context.cultureFormatter.formatCurrency(grossAmount.toString(), props.context.request.channel?.Currency);
    return {
        itemCount: (
            <Node className='__sales-order-count' tag='div'>
                <Node className='label' tag='span'>
                    {itemsCount}
                </Node>
                <Node className='value' tag='span'>
                    {itemsCount === 1 ? itemLabel : itemsLabel}
                </Node>
            </Node>
        ),
        grossAmount: (
            <Node className='__sales-order-total' tag='div'>
                <Node className='label' tag='span'>
                    {orderTotalLabelText}
                </Node>
                <Node className='value' tag='span'>
                    {totalAmount}
                </Node>
            </Node>
        )
    };
};

export const OrderConfirmationOrderInfomation: React.FC<IOrderConfirmationViewProps & IOrderInformation & ISalesInfoProps & IOrderDate> = props => {
    const { orderInformationProps, channelReferenceId, itemCount, grossAmount, dateLabel, orderCreatedDate } = props;
    const { isAuthenticated } = props?.["context"].actionContext.requestContext.user;
    const orderno_no = get(channelReferenceId, 'props.children[0].props.children[1].props.children');
    const itemCount_str = get(itemCount, 'props.children[1].props.children');
    const itemCount_no = get(itemCount, 'props.children[0].props.children');
    const gross_amount = get(grossAmount, 'props.children[1].props.children');
    if(isAuthenticated){
        return(
            <Node {...orderInformationProps}>
                {/* VSI Customization - START - 23/11/20 */}
                {/* <div>
                    {orderno_str}
                    {orderno_no}
                </div> */}
                                
                {/* VSI Customization - END - 27/11/20 */}
                {channelReferenceId}
                <div className='__order-brief-info'>
                    <div className='__order-information-created-date ss'>
                        <Node className='__created-date' tag='span'>
                            <strong>{dateLabel}</strong>
                        </Node>
                        <Node className='__created-date-label' tag='span'>
                            {orderCreatedDate}
                        </Node>
                    </div>
    
                    <div className='count-ammount'>
                        {itemCount}
                        {grossAmount}
                    </div>
                </div>
                
            </Node>
        )
    }else{
        return(
            <>
                <div className="row">
                    <div className="col-12">
                        <div className="order-number__wrap">
                            <h3>Order number: <span>{orderno_no}</span></h3>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="order-date__wrap">
                            <h5>Order date: <span>{orderCreatedDate}</span></h5>
                        </div>
                    </div>
                    <div className="col-12 col-md-6">
                        <div className="item-order__total">
                            <p>{itemCount_no} {itemCount_str} | <span>Order total: {gross_amount}</span></p>
                        </div>
                    </div>
                </div>
            </>
        )
    }
};

/* VSI Customization - START - 23/11/20 - Removed OrderConfirmationSalesLine FC -  END */

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
    </Node>
);
export const OrderConfirmationGroupDeliveryGuest = (props: IOrderConfirmationViewProps) => {

    const delivery_ship = props.groups?.groups.find(addr => {
    const deliveryAddress = addr.delivery.address as DeliveryAddressShip | undefined;
    const deliveryType = deliveryAddress?.props?.deliveryType;
    return deliveryType && deliveryType.trim().toLowerCase() === "ship";
});

const delivery_cnc = props.groups?.groups.find(addr => {
    const deliveryAddress = addr.delivery.address as DeliveryAddressShip | undefined;
    const deliveryType = deliveryAddress?.props?.deliveryType;
    return deliveryType && (deliveryType.trim().toLowerCase() === "click and collect" || deliveryType.trim().toLowerCase() === 'pickup');
});
    const bothDeliveries =  [delivery_ship, delivery_cnc];
    return(
        <div className="row">
            {bothDeliveries.length && bothDeliveries?.map(addr => {
                const deliveryAddressShip = addr?.delivery.address as DeliveryAddressShip | undefined;
                if(addr && deliveryAddressShip?.props?.deliveryType === "ship"){
                    const address_name = deliveryAddressShip?.props?.address.Name
                    const address_street = deliveryAddressShip?.props?.address.Street
                    const address_city = deliveryAddressShip?.props?.address.City
                    const address_zipcode = deliveryAddressShip?.props?.address.ZipCode
                    const address_phone = deliveryAddressShip?.props?.address.Phone
                    return (
                        <div className="col-12 col-md-6">
                            <div className="order-delivery__wrap">
                                <h5>Home delivery address:</h5>
                                <p>
                                    {address_name}<br />
                                    {address_street} {address_city} {address_zipcode}<br />
                                    Phone: {address_phone} <br />
                                    Email address: {props.salesOrder?.ReceiptEmail ?  props.salesOrder?.ReceiptEmail : ""}
                                </p>
                            </div>
                        </div>
                    )
                }else{
                    return <></>
                }
            })}
        </div>
) 
};
export const OrderConfirmationGroupPickupGuest = (props: IGroupDelivery) => {
    const address = props.address as { props: { address: { City: string; FullAddress: string } } } | undefined;
    const address_city = address?.props.address.City;
    const Full_address = address?.props.address.FullAddress;
    return(
        <div className="row">
            <div className="">
                <div className="">
                    <h5>
                        Collection store: <span>{address_city}</span>
                        <p>
                        {Full_address}
                        {/* {address_street}, Lasswade Edinburgh<br />
                        EH18 1AZ GBR<br /> */}
                        </p>
                    </h5>
                </div>
            </div>
        </div>
) 
};
// Just to display address against group i.e. only picked up group
export const OrderConfirmationGroup: React.FC<IGroup> = props => {
    const { groupProps, delivery } = props;
    return (
        <Node {...groupProps}>
            {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
            {/* VSI Customization - START - 23/11/20 - Displayed sales line in OrderConfirmationGroups function and removed from here - VSI Customization - END */}
        </Node>
    );
};

/* VSI Customization - START - 23/11/20 */
// Following function displays sales line items
const _displaySalesLine = (
    salesLineItem: SalesLine,
    simpleProduct: SimpleProduct,
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>
): React.ReactElement => {
    const { resources } = props;
    // resources required for ICartlineItem component
    const _resources = {
        discountStringText: resources.discountStringLabel,
        sizeString: resources.productDimensionSize,
        colorString: resources.productDimensionColor,
        configString: resources.productDimensionConfiguration,
        styleString: resources.productDimensionStyle,
        quantityDisplayString: resources.quantityLabel,
        inputQuantityAriaLabel: resources.quantityLabel,
        originalPriceText: resources.originalPriceLabel,
        currentPriceText: resources.currentPriceLabel,
        shippingChargesText: resources.shippingChargesLabel
    };
    const _imageSettings = {
        viewports: {
            xs: { q: `w=50&h=50&m=6`, w: 0, h: 0 },
            xl: { q: `w=50&h=50&m=6`, w: 0, h: 0 }
        }
    };
    return (
        <CartLine
            data={{
                cartLine: salesLineItem,
                product: simpleProduct
            }}
            currentQuantity={salesLineItem.Quantity!}
            maxQuantity={props.app.config.maxQuantityForCartLineItem}
            isOutOfStock={false}
            gridSettings={props.context.request.gridSettings!}
            id={props.id}
            typeName={props.typeName}
            productUrl={getProductUrlSync(simpleProduct, props.context.actionContext, undefined)}
            context={props.context}
            imageSettings={props.config.imageSettings || _imageSettings}
            resources={_resources}
            primaryImageUrl={simpleProduct.PrimaryImageUrl}
            errorMessage={''}
            hideQuantityControls={true}
            isOrderConfirmation={true}
            isQuantityEditable={false}
            isSalesLine={true}
        />
    );
};
// Following function displayes price with each sales line
const _displaySalesLinePrice = (
    salesLineItem: SalesLine,
    props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>
): React.ReactElement => {
    const {
        id,
        context,
        typeName,
        resources: { originalPriceLabel, currentPriceLabel }
    } = props;
    return (
        <Node tag='div' className='msc-cart-line-item-subtotal-amount'>
            <PriceComponent
                data={
                    salesLineItem.NetAmountWithoutTax
                        ? {
                              price: {
                                  CustomerContextualPrice: salesLineItem.NetAmountWithoutTax,
                                  BasePrice: salesLineItem.NetPrice
                              }
                          }
                        : {
                              price: {
                                  CustomerContextualPrice: salesLineItem.NetAmountWithAllInclusiveTax,
                                  BasePrice: salesLineItem.Price
                              }
                          }
                }
                context={context}
                id={id}
                typeName={typeName}
                className='discount-value-line'
                originalPriceText={originalPriceLabel}
                currentPriceText={currentPriceLabel}
            />
        </Node>
    );
};
/* VSI Customization - END */

// Converted functional component to simple function to pass order confirmation props to it
// This functions just displays picked up group
const _renderOrderConfirmationGroup = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>, group: IGroup, pickUpOrderNumber: number) => {
    const { salesLines, salesLinesProps, groupProps, delivery } = group;
    const { isAuthenticated } = props.context.request.user;
    return (
        <>
            {salesLines && salesLines.length > 0 && (
                <Node {...groupProps}>
                    {delivery && isAuthenticated ? <OrderConfirmationGroupDelivery {...delivery} /> : <OrderConfirmationGroupPickupGuest {...delivery} />}
                    <Node {...salesLinesProps}>
                        {salesLines.map((salesLine: ISalesLine, index: number) => {
                            const salesLineItem = salesLine.data.salesLine;
                            const simpleProduct = salesLine.data.product;
                            return (
                                <Node className='sales-line-section' tag='div' key={index}>
                                    {simpleProduct && _displaySalesLine(salesLineItem, simpleProduct, props)}
                                    {_displaySalesLinePrice(salesLineItem, props)}
                                </Node>
                            );
                        })}
                    </Node>
                </Node>
            )}
        </>
    );
};

// Following function displays picked up groups only
const _renderPickedOrders = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>, groups: IGroups) => {
    const {
        resources: { clickandCollectLabel, pickedOrderIndex }
    } = props;
    // filter pickedup groups
    const filteredPickedupGroups = groups.groups.filter(group => {
        const deliveryType = group.data.deliveryType.trim().toLowerCase();
        return deliveryType === 'click and collect' || deliveryType === 'pickup' || deliveryType !== 'ship';
    });
    const pickedUpCount = filteredPickedupGroups.length;
    return (
        <>
            {filteredPickedupGroups.map((group: IGroup, index: number) => {
                const pickUpOrderNumber = index + 1;
                const _orderIndex = pickedOrderIndex && pickedOrderIndex.replace('{index}', pickUpOrderNumber.toString());
                return (
                    <Node className='ms-pickedup-products' tag='div' key={index}>
                        <Node tag='div' className='ms-heading row delivery-dispatched-details'>
                            <Node tag='p' className='click-collect-label'>
                                {clickandCollectLabel}
                            </Node>
                            {pickedUpCount > 1 && (
                                <Node tag='p' className='order-index'>
                                    {_orderIndex}
                                </Node>
                            )}
                        </Node>
                        {_renderOrderConfirmationGroup(props, group, pickUpOrderNumber)}
                    </Node>
                );
            })}
        </>
    );
};

/* VSI Customization - END */
const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<{}>> = props => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        salesLines,
        context: { actionContext },
        slots: { helpCentreBlock, textBlockBanner },
        data: { orderHydration },
        resources: { orderPlacedOnLabel, dobbiesLabel, notAvailableText }
    } = props;
    const { isAuthenticated } = props.context.request.user;
    /* VSI Customization - START - 23/11/20 */
    const [vendorProducts, SetVendorProducts] = useState<SimpleProduct[]>([]);
    const [vendorsInfo, SetVendorInfo] = useState<IVendorProducts[]>([]);
    const [deliveryGroup, SetDeliveryGroup] = useState(-1);
    const [pickedUpGroup, SetPickedUpGroup] = useState<number[]>([]);
    const salesOrderInfo = _getCountandPrice(props);
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    const products = orderHydration && orderHydration.result && orderHydration.result.products;
    useEffect(() => {
        salesLines &&
            salesLines.length > 0 &&
            _getProductsVendorInfo(
                actionContext,
                products,
                salesLines,
                dobbiesLabel,
                notAvailableText,
                SetVendorProducts,
                SetVendorInfo,
                vendorProducts,
                vendorsInfo
            ).catch(err => {
                return;
            });
        _configureAddresses(groups, SetPickedUpGroup, pickedUpGroup, SetDeliveryGroup, deliveryGroup);
    }, []);
    const createdDateTime = salesOrder && salesOrder.SalesLines && salesOrder.SalesLines[0] && salesOrder.SalesLines[0].SalesDate;
    const locale = get(props, 'context.request.locale') || 'en-US';
    const orderCreatedDate = (createdDateTime && new Date(createdDateTime).toLocaleDateString(locale)) || '';
    const created = {
        dateLabel: orderPlacedOnLabel,
        orderCreatedDate: orderCreatedDate
    };
    // home delivery address shown at top
    const deliveryDetails = deliveryGroup !== -1 && groups && groups.groups[deliveryGroup].delivery;
    /* VSI Customization - END - 18/12/20 */
    return (
        <Module {...moduleProps}>
            <Node tag='div' className={`row ${isAuthenticated ? "signin-order-confirmation" : "signout-order-confirmation"}`}>
                {isAuthenticated && 
                <Node tag='div' className='col-12'>
                    {orderInfomation && heading}
                </Node>}
                <Node tag='div' className='col-lg-8 order-confirmation-alldetails'>
                    {alert}
                    {loading}
                    {/* VSI Customization - START - 23/11/20 */}
                    {!isAuthenticated &&
                    <div className='row'>
                        <Node tag='div' className='col-12'>
                            {orderInfomation && heading}
                        </Node>
                    </div>}
                    {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} {...salesOrderInfo} {...created} {...props} />}
                    {deliveryDetails && isAuthenticated ? <OrderConfirmationGroupDelivery {...deliveryDetails} /> : <OrderConfirmationGroupDeliveryGuest {...props} /> }
                    {/* Following function just displays shipped items */}
                    {vendorsInfo && _displayVendorProducts(props, vendorsInfo)}
                    {/* Following functions just displays pickedup items */}
                    {groups && _renderPickedOrders(props, groups)}
                    {/* VSI Customization - END - 23/12/20 */}
                </Node>
                <Node tag='div' className='col-lg-4'>
                    {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} />}
                    {/* {_orderConfirmationOrderSummary(orderSummary, props.salesOrder)} */}
                    {payment && <OrderConfirmationPayment {...payment} />}
                    {/* VSI Customization - START - 23/11/20 */}
                    {orderInfomation && (
                        <Node className='help-centre-block' tag='div'>
                            {helpCentreBlock}
                        </Node>
                    )}
                    {!isAuthenticated &&
                    <Node className='credit-availability-banner' tag='div'>
                        {textBlockBanner}
                    </Node>}
                    {/* VSI Customization - END */}
                    {_orderConfirmationHelp(help)}
                </Node>
            </Node>
        </Module>
    );
};

/* VSI Customization - START - 18/12/20 */
const _displayHeadingRow = (
    resources: IOrderConfirmationResources,
    vendorInfo: IVendorProducts | undefined,
    index: number,
    total: number
) => {
    const { deliveryHeading, shippedOrderIndex, dispatchedByLabel, dobbiesLabel, shippingTimeLabel } = resources;
    const orderIndex = shippedOrderIndex && shippedOrderIndex.replace('{index}', index.toString()).replace('{total}', total.toString());
    return (
        <Node tag='div' className='ms-heading row delivery-dispatched-details'>
            <Node tag='div' className='delivery-dispatched-details__count'>
                <Node tag='span' className='__heading'>
                    {deliveryHeading}{' '}
                </Node>
                {total > 1 && (
                    <Node tag='span' className='__index'>
                        {orderIndex}
                    </Node>
                )}
            </Node>
            <Node tag='div' className='delivery-dispatched-details__right'>
                <Node tag='div' className='dispatched-by-vendor'>
                    <Node tag='span' className='label'>
                        {dispatchedByLabel}{' '}
                    </Node>
                    <Node tag='span' className='vendor-name'>
                        {vendorInfo ? vendorInfo.name : dobbiesLabel}
                    </Node>
                </Node>
                {vendorInfo && vendorInfo.time && (
                    <Node tag='div' className='estimated-shipping-time'>
                        <Node tag='span' className='label'>
                            {shippingTimeLabel}{' '}
                        </Node>
                        <Node tag='span' className='vendor-name'>
                            {vendorInfo.time}
                        </Node>
                    </Node>
                )}
            </Node>
        </Node>
    );
};
// Display shipped products
const _displayVendorProducts = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>, vendorsInfo: IVendorProducts[]) => {
    return(
    <Node tag='div' className='ms-vendor-shipped-products'>
        {vendorsInfo &&
            vendorsInfo.map((vendorInfo: IVendorProducts, index: number) => {
                // filter shipped products from each sales line
                const vendorsSalesLines =
                    props.salesLines &&
                    props.salesLines.filter(salesLine => {
                        const salesLineProductID = salesLine.data.product && salesLine.data.product.RecordId;
                        // filtering vendor's product of current index of vendorInfo
                        const isSameVendor = vendorInfo.productList.find(vendorProductID => {
                            return vendorProductID === salesLineProductID;
                        });
                        return isSameVendor && salesLine;
                    });
                const currentIndex = index + 1; // 1..n instead of 0...n-1
                return (
                    <Node tag='div' className='ms-dobies-products' key={index}>
                        {_displayHeadingRow(props.resources, vendorInfo, currentIndex, vendorsInfo.length)}
                        {vendorsSalesLines &&
                            vendorsSalesLines.map((vendorsSalesLine, salesIndex) => {
                                const salesLineItem = vendorsSalesLine.data.salesLine;
                                const simpleProduct = vendorsSalesLine.data.product;
                                return (
                                    <Node className='sales-line-section' tag='div' key={index}>
                                        {simpleProduct && _displaySalesLine(salesLineItem, simpleProduct, props)}
                                        {_displaySalesLinePrice(salesLineItem, props)}
                                    </Node>
                                );
                            })}
                    </Node>
                );
            })}
    </Node>
)};

// This function stores picked up groups in an array if any and stores delivery address to display on top if any
const _configureAddresses = (
    groups: IGroups | undefined,
    SetPickedUpGroup: React.Dispatch<React.SetStateAction<number[]>>,
    pickedUpGroup: number[],
    SetDeliveryGroup: React.Dispatch<React.SetStateAction<number>>,
    deliveryGroup: number
) => {
    if (pickedUpGroup.length > 0 || deliveryGroup !== -1) {
        return;
    }
    let deliveryAddressGroup = -1;
    const pickedUpList: number[] = [];
    groups &&
        groups.groups.map((group: IGroup, index: number) => {
            const addresstype = group.data.deliveryType.trim().toLowerCase();
            if (addresstype === 'ship') {
                deliveryAddressGroup = index;
            } else if (addresstype === 'pickup') {
                pickedUpList.push(index);
            }
        });
    if (deliveryAddressGroup !== deliveryGroup) {
        SetDeliveryGroup(deliveryAddressGroup);
    }
    if (pickedUpList !== pickedUpGroup) {
        SetPickedUpGroup(pickedUpList);
    }
};

// Returns array filled with vendor and their product details
const _getVendorInfo = (
    hybridProducts: SimpleProduct[],
    vendorsProducts: SimpleProduct[],
    salesLines: ISalesLine[],
    dobbiesLabel: string
): IVendorProducts[] => {
    if (hybridProducts && hybridProducts.length && hybridProducts.length > 0) {
        const vendorList: IVendorProducts[] = [];
        // store all vendors name or undefined if not
        vendorsProducts &&
            vendorsProducts.map(vendorsProduct => {
                const vendorNameProperty =
                    vendorsProduct.ExtensionProperties &&
                    vendorsProduct.ExtensionProperties.find(property => {
                        const propertykey = property.Key && property.Key.trim().toLowerCase();
                        return propertykey === 'vendorname';
                    });
                const vendorName = vendorNameProperty && vendorNameProperty.Value && vendorNameProperty.Value.StringValue;
                // check if vendor name is already stored in vendor list
                const isDuplicate = vendorList.find(vendor => {
                    return vendor && vendor.name === vendorName;
                });
                if (!isDuplicate) {
                    const shippingTimeProperty =
                        vendorsProduct.ExtensionProperties &&
                        vendorsProduct.ExtensionProperties.find(property => {
                            const propertykey = property.Key && property.Key.trim().toLowerCase();
                            return propertykey === 'estimatedshippingtime';
                        });
                    const shippingTime = shippingTimeProperty && shippingTimeProperty.Value && shippingTimeProperty.Value.StringValue;
                    // avoid duplicate vendor names
                    vendorList.push({ name: vendorName, time: shippingTime, productList: [] });
                }
            });
        // to store dobbies products, we need to check first vendor products
        const vendorsProductList: number[] = [];
        // set respective products indexes along with vendor name
        vendorList.map((vendorName: IVendorProducts, index: number) => {
            vendorsProducts &&
                vendorsProducts.map((product: SimpleProduct, pindex: number) => {
                    // returns products having vendor name matched with vendorList created above
                    product.ExtensionProperties &&
                        product.ExtensionProperties.map(extensionProperty => {
                            const key = extensionProperty.Key && extensionProperty.Key.trim().toLowerCase();
                            const value = extensionProperty.Value && extensionProperty.Value.StringValue;
                            if (key === 'vendorname' && value === vendorName.name) {
                                vendorList[index].productList.push(product.RecordId);
                                vendorsProductList.push(product.RecordId);
                            }
                        });
                });
        });
        // Filter shipped salesLines
        const shippedSalesLines = salesLines.filter(salesLine => {
            const deliveryType = salesLine.data.deliveryType.trim().toLowerCase();
            return deliveryType === 'ship';
        });
        // DOBBIES product list
        const dobbiesProducts: number[] = [];
        hybridProducts.map(hybridProduct => {
            const recordID = hybridProduct.RecordId;
            // exclude vendor products to add in dobbies' list
            const isVendorProduct = vendorsProductList.find(vendorProductId => {
                return recordID === vendorProductId;
            });
            if (isVendorProduct === undefined) {
                // exclude pickup products from dobbies to store only shipped items against it
                const isShipped = shippedSalesLines.find(salesLine => {
                    const productID = salesLine.data.product && salesLine.data.product.RecordId;
                    return recordID === productID;
                });
                if (isShipped) {
                    dobbiesProducts.push(hybridProduct.RecordId);
                }
            }
        });
        if (dobbiesProducts.length !== 0) {
            // add dobbies shipped items at the end of array
            const dobbiesProductObj: IVendorProducts = { name: dobbiesLabel, time: undefined, productList: dobbiesProducts };
            vendorList.push(dobbiesProductObj);
        }
        return vendorList;
    }
    return [];
};
export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

// Get vendor attributes using data action and hybrids products with information
const _getProductsVendorInfo = async (
    actionContext: IActionContext,
    products: SimpleProduct[] | undefined,
    salesLines: ISalesLine[],
    dobbiesLabel: string,
    notAvailableText: string,
    SetVendorProducts: React.Dispatch<React.SetStateAction<SimpleProduct[]>>,
    SetVendorInfo: React.Dispatch<React.SetStateAction<IVendorProducts[]>>,
    vendorsProducts: SimpleProduct[],
    vendorsInfo: IVendorProducts[]
) => {
    const callerContext = actionContext;
    const {
        requestContext: {
            apiSettings: { channelId, catalogId }
        }
    } = actionContext;
    if (products) {
        const _hybridProducts: SimpleProduct[] = products;
        const vendorProducts: SimpleProduct[] = [];
        const searchCriteriaInput: ProductSearchCriteria = {};
        searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
        const productIds: number[] = [];
        products.map(product => {
            productIds.push(product.RecordId);
        });
        searchCriteriaInput.Ids = productIds;
        searchCriteriaInput.IncludeAttributes = true;
        const productsData = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);
        productsData.map((product: ProductSearchResult, index: number) => {
            const attributes = product.AttributeValues;
            const isvendorShippedAttribute =
                attributes &&
                attributes.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });
            const isVendorShiped =
                isvendorShippedAttribute &&
                isvendorShippedAttribute.TextValue &&
                isvendorShippedAttribute.TextValue.trim().toLowerCase() === 'yes';
            const extensionProperties: CommerceProperty[] = [];
            if (isVendorShiped) {
                const selectedAttributes =
                    attributes &&
                    attributes.filter(attribute => {
                        const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                        const isVendorNameAvailable = attribute.TextValue; // if vendor name is not undefined
                        return (
                            attributeName &&
                            isVendorNameAvailable &&
                            (attributeName === 'vendorname' || attributeName === 'estimatedshippingtime')
                        );
                    });
                if (selectedAttributes && selectedAttributes.length > 0) {
                    selectedAttributes &&
                        selectedAttributes.map(selectedAttribute => {
                            const commercePropertyValue: CommercePropertyValue = { StringValue: selectedAttribute.TextValue };
                            const commerceProperty: CommerceProperty = { Key: selectedAttribute.Name, Value: commercePropertyValue };
                            extensionProperties.push(commerceProperty);
                            // hybrid all products
                            _hybridProducts[index].ExtensionProperties = extensionProperties;
                            // no duplicate vendor products
                            const isDuplicate =
                                vendorProducts &&
                                vendorProducts.find(vendorProduct => {
                                    return vendorProduct.RecordId === product.RecordId;
                                });
                            // if product is vendorshipped, store it in array
                            !isDuplicate && vendorProducts.push(_hybridProducts[index]);
                        });
                } else {
                    // When product is vendor shipped but vendor info is not available
                    const vendorname: CommerceProperty = { Key: 'vendorname', Value: { StringValue: notAvailableText } };
                    const estimatedShippingTime: CommerceProperty = {
                        Key: 'estimatedshippingtime',
                        Value: { StringValue: notAvailableText }
                    };
                    extensionProperties.push(vendorname);
                    extensionProperties.push(estimatedShippingTime);
                    // hybrid all products
                    _hybridProducts[index].ExtensionProperties = extensionProperties;
                    // no duplicate vendor products, merge product with same vendor
                    const isDuplicate =
                        vendorProducts &&
                        vendorProducts.find(vendorProduct => {
                            return vendorProduct.RecordId === product.RecordId;
                        });
                    // if product is vendorshipped, store it in array
                    !isDuplicate && vendorProducts.push(_hybridProducts[index]);
                }
            }
        });

        if (!vendorsProducts || vendorsProducts.length === 0) {
            SetVendorProducts(vendorProducts);
        }
        const _vendorsInfo = _hybridProducts && _getVendorInfo(_hybridProducts, vendorProducts, salesLines, dobbiesLabel);
        if (!vendorsInfo || vendorsInfo.length === 0) {
            SetVendorInfo(_vendorsInfo);
        }
    }
};
/* VSI Customizatoin - END */

export default OrderConfirmationView;
